
import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SchoolIcon from '@mui/icons-material/School';
import SpeedIcon from '@mui/icons-material/Speed';

function Home() {
  const navigate = useNavigate();

  const features = [
    {
      title: 'Simulated Trading Environment',
      description: 'Every participant trades on demo accounts under real market conditions. Our system measures your performance against transparent parameters.',
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
    },
    {
      title: 'Multi-Level Evaluation',
      description: 'Progress through our evaluation phases by meeting specific objectives. Each level helps refine your strategy and consistency.',
      icon: <CheckCircleIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
    },
    {
      title: 'Performance Rewards (Simulation Credits)',
      description: 'Qualified traders may earn simulation-based rewards or recognition within our platform, based purely on performance.',
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
    },
    {
      title: 'Education & Development',
      description: 'Learn risk control, psychology, and consistency through experience — not guesswork.',
      icon: <SchoolIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
    }
  ];

  const whyChooseUs = [
    {
      feature: 'Fast Access',
      description: 'Get your simulated evaluation credentials within 24 hours after registration.'
    },
    {
      feature: 'Transparent Metrics',
      description: 'All evaluation criteria and targets are clearly stated before you begin.'
    },
    {
      feature: 'Skill-Based Progression',
      description: 'Move through each stage purely based on your trading discipline and consistency.'
    },
    {
      feature: 'Fair & Realistic Conditions',
      description: 'Trade under live market conditions with institutional-grade spreads and execution.'
    },
    {
      feature: 'Community & Support',
      description: 'Get assistance via email — our support is always one message away.'
    }
  ];

  const steps = [
    {
      step: 'Step 1',
      title: 'Register for an Evaluation',
      description: 'Choose your preferred account size and complete your registration form.'
    },
    {
      step: 'Step 2',
      title: 'Receive Your Credentials',
      description: "You'll receive access to a simulated trading account via email within 24 hours."
    },
    {
      step: 'Step 3',
      title: 'Trade & Demonstrate Performance',
      description: 'Trade using your own strategy while adhering to predefined drawdown and target rules.'
    },
    {
      step: 'Step 4',
      title: 'Progress to Higher Simulations',
      description: 'Meet your targets? You move to the next phase with increased evaluation scope.'
    },
    {
      step: 'Step 5',
      title: 'Recognition & Rewards',
      description: 'Top performers are recognized with internal rewards, simulation credits, and eligibility for advanced evaluations.'
    }
  ];

  const testimonials = [
    {
      text: 'X Funding gave me a structured way to test my consistency and discipline. The evaluation process helped me identify weaknesses in my strategy and build stronger trading habits. It feels like a real professional environment built for traders who take their craft seriously.',
      author: 'Rahul Sharma'
    },
    {
      text: 'The evaluation system is transparent and well-organized. What stood out to me was how the rules encourage risk management and patience - two things every trader needs. I have become more methodical in my approach thanks to the experience.',
      author: 'Sanya Thomas'
    },
    {
      text: 'I joined X Funding to challenge myself and it exceeded expectations. The environment, feedback, and structure made me think like a professional. It is one of the few platforms that actually helps you grow as a disciplined trader.',
      author: 'Arjun Mehta'
    }
  ];

  const programHighlights = [
    '2-Step Evaluation Process (Challenge + Verification)',
    'Profit Target: 10% (Phase 1), 5% (Phase 2)',
    'Maximum Drawdown: 10%',
    'Minimum Trading Days: 3',
    'Trade Duration: Unlimited',
    'Evaluation Platforms: MT4 / MT5 (Simulated Accounts Only)',
    'Fee: One-time simulation access cost (non-refundable service charge)'
  ];

  return (
    <Box sx={{ bgcolor: '#131722', minHeight: '100vh', py: 6 }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ fontWeight: 700, color: '#fff', mb: 3 }}>
            X FUNDING
          </Typography>
          <Typography variant="h5" sx={{ color: '#d1d4dc', mb: 1, maxWidth: 800, mx: 'auto' }}>
            Prove your trading skills with X Funding and get funded up to $100,000.
          </Typography>
          <Typography variant="h5" sx={{ color: '#d1d4dc', mb: 4, maxWidth: 800, mx: 'auto' }}>
            No risk, just opportunity.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signin')}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              backgroundColor: '#4ade80 !important', 
              color: '#1e293b',
              background: '#4ade80 !important',
              '&:hover': {
                backgroundColor: '#22c55e !important',
                background: '#22c55e !important',
                color: '#1e293b'
              }
            }}
          >
            Start Challenge
          </Button>
        </Box>

        {/* About Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 3, textAlign: 'center' }}>
            About X Funding
          </Typography>
          <Card sx={{ background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)', mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                X Funding empowers traders to demonstrate skill and discipline through our simulated evaluation environment. Successful participants can qualify for higher simulated account levels and performance-based rewards.
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ background: 'linear-gradient(135deg, #1f4068 0%, #162447 100%)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
                Empowering Traders with Opportunity, Not Risk.
              </Typography>
              <Typography variant="body1" sx={{ color: '#d1d4dc' }}>
                At X Funding, we believe skilled traders deserve a platform to showcase their talent without risking their personal funds. We provide a simulated trading environment that mirrors real market conditions — where your results are tracked and evaluated based on risk management, consistency, and profitability. Our mission is to help traders develop discipline, professionalism, and sustainable trading habits that can prepare them for future opportunities in real-world financial markets.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* What We Offer Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 2, textAlign: 'center' }}>
            What We Offer
          </Typography>
          <Typography variant="h6" sx={{ color: '#3b82f6', mb: 4, textAlign: 'center' }}>
            Transparent. Structured. Growth-Focused.
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  bgcolor: '#1e222d',
                  border: '1px solid #2a2e39',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    borderColor: '#3b82f6'
                  }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, ml: 2 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#d1d4dc' }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Why Choose Us Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 4, textAlign: 'center' }}>
            Why Traders Choose X Funding
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1e222d', border: '1px solid #2a2e39' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(59,130,246,0.1)' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #3b82f6', width: '30%' }}>
                    Feature
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #3b82f6' }}>
                    Description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {whyChooseUs.map((item, index) => (
                  <TableRow 
                    key={index}
                    sx={{ 
                      '&:hover': { bgcolor: 'rgba(59,130,246,0.05)' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ 
                      color: '#fff', 
                      fontWeight: 600, 
                      fontSize: 16,
                      borderBottom: '1px solid #2a2e39',
                      py: 2
                    }}>
                      {item.feature}
                    </TableCell>
                    <TableCell sx={{ 
                      color: '#d1d4dc', 
                      fontSize: 16,
                      borderBottom: '1px solid #2a2e39',
                      py: 2
                    }}>
                      {item.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* How It Works Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 4, textAlign: 'center' }}>
            How the Evaluation Works
          </Typography>
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {steps.map((step, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Card sx={{ 
                  bgcolor: '#1e222d',
                  border: '2px solid #2a2e39',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    borderColor: '#3b82f6'
                  }
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: -20,
                    left: 20,
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 18,
                    color: '#fff',
                    border: '3px solid #131722'
                  }}>
                    {index + 1}
                  </Box>
                  <CardContent sx={{ pt: 5, pl: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                        {step.step}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        — {step.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#d1d4dc', pl: 0 }}>
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Program Highlights Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 4, textAlign: 'center' }}>
            Program Highlights
          </Typography>
          <Card sx={{ background: 'linear-gradient(135deg, #162447 0%, #1f4068 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {programHighlights.map((highlight, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleIcon sx={{ color: '#4ade80', fontSize: 24 }} />
                    <Typography variant="body1" sx={{ color: '#fff' }}>
                      {highlight}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
              <Typography variant="body2" sx={{ color: '#d1d4dc', fontStyle: 'italic' }}>
                Note: All trading activity takes place on demo accounts with simulated data. No real trading or investment services are offered.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Testimonials Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#fff', mb: 4, textAlign: 'center' }}>
            Testimonials
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 3, 
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': {
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: '#1e222d',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#3b82f6',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#2563eb'
              }
            }
          }}>
            {testimonials.map((testimonial, index) => (
              <Card key={index} sx={{ 
                minWidth: 350,
                maxWidth: 350,
                height: 'auto',
                bgcolor: '#1e222d',
                border: '1px solid #2a2e39',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0
              }}>
                <CardContent sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  p: 3
                }}>
                  <Typography variant="body1" sx={{ color: '#d1d4dc', mb: 3, fontStyle: 'italic', lineHeight: 1.6 }}>
                    "{testimonial.text}"
                  </Typography>
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="h6" sx={{ color: '#3b82f6', fontWeight: 600, textAlign: 'right' }}>
                      - {testimonial.author}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Disclaimer Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Card sx={{ bgcolor: '#1e222d', border: '1px solid #2a2e39' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                <strong>Compliance Disclaimer:</strong> X Funding is an educational and evaluation platform offering simulated trading programs. We do not provide financial advice, investment opportunities, brokerage services, or access to live funds. All evaluations are conducted using demo accounts for educational and performance assessment purposes only. Participation fees cover the cost of data access, software, and evaluation management. Results or performance in simulated trading do not guarantee future results in live markets.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
}

export default Home;
